/**
 * AI Scribe V2 — session orchestration
 *
 * Wraps `usePcmAudioStream`, `useWebSocket` and `useTimer` hooks so the V2 UI
 * only deals with `status / transcript / clinicalEvents / elapsed`.
 *
 * Transport: the streaming ASR socket decodes a raw sample stream, so audio goes
 * out as continuous 16-bit LE PCM / mono / 16 kHz frames — not the V1
 * `audio/webm` blobs, which that endpoint cannot decode.
 *
 * @author [Rajkumar Rathod]
 */

import { usePcmAudioStream } from "@/hooks/ai-scriber/use-pcm-audio-stream";
import useWebSocket from "@/hooks/use-web-socket";
import { usePatientScribeContext } from "@/queries/ai-scriber/use-patient-scribe-context";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTimer } from "../useTimer";
import { SCRIBE_V2_AUDIO_SOCKET_URL } from "./constants";
import {
  ClinicalEvent,
  ScribeSessionSnapshot,
  ScribeStatus,
  ScribeTranscriptLine,
} from "./types";
import { normaliseScribeFrame } from "./utils";

interface UseScribeSessionOptions {
  /** Display name for provider bubbles */
  providerName?: string;
  /** Display name for patient bubbles */
  patientName?: string;
  /** Patient the session belongs to — used to pull the scribe's chart context. */
  patientUuid?: string;
  /**
   * Snapshot of a session interrupted by a refresh/offline. When present the
   * hook rehydrates its transcript, events and elapsed time and comes back
   * paused — audio cannot silently re-arm, so the provider resumes it.
   */
  initialSnapshot?: ScribeSessionSnapshot | null;
}

export const useScribeSession = ({
  providerName,
  patientName,
  patientUuid,
  initialSnapshot,
}: UseScribeSessionOptions = {}) => {
  const wasRestored = !!initialSnapshot;
  const { time, startTimer, stopTimer, pauseTimer, resumeTimer } = useTimer(
    initialSnapshot?.elapsedSeconds ?? 0
  );

  const [status, setStatus] = useState<ScribeStatus>(
    wasRestored ? "PAUSED" : "IDLE"
  );
  const [transcript, setTranscript] = useState<ScribeTranscriptLine[]>(
    initialSnapshot?.transcript ?? []
  );
  const [clinicalEvents, setClinicalEvents] = useState<ClinicalEvent[]>(
    initialSnapshot?.clinicalEvents ?? []
  );

  /** Wall-clock anchor used to turn socket offsets into "5:18pm" labels. */
  const sessionStartedAtRef = useRef<number>(
    initialSnapshot?.sessionStartedAt ?? Date.now()
  );
  /** Tracks whether a session already ran, so we reconnect instead of reusing a closed socket. */
  const hasRecordedOnceRef = useRef<boolean>(false);
  /** A restored session has no live audio graph, so its first resume must re-arm capture. */
  const needsCaptureRestartRef = useRef<boolean>(wasRestored);
  /** Speaker names change across renders but the socket handler is memoised once. */
  const namesRef = useRef({ providerName, patientName });
  namesRef.current = { providerName, patientName };
  /** Ids already rendered — the backend may resend an event as it refines it. */
  const seenEventIdsRef = useRef<Set<string>>(
    new Set((initialSnapshot?.clinicalEvents ?? []).map((item) => item.id))
  );
  /** Lines rendered so far — seeds speaker alternation without reading state. */
  const lineCountRef = useRef<number>(initialSnapshot?.transcript.length ?? 0);

  const handleFrame = useCallback((event: MessageEvent<unknown>) => {
    if (!event?.data) return;

    const update = normaliseScribeFrame(event.data, {
      sessionStartedAt: sessionStartedAtRef.current,
      lineOffset: lineCountRef.current,
      providerName: namesRef.current.providerName,
      patientName: namesRef.current.patientName,
    });

    if (update.lines.length) {
      lineCountRef.current += update.lines.length;
      setTranscript((previous) => [...previous, ...update.lines]);
    }

    if (update.events.length) {
      const freshEvents = update.events.filter(
        (item) => !seenEventIdsRef.current.has(item.id)
      );
      freshEvents.forEach((item) => seenEventIdsRef.current.add(item.id));
      if (freshEvents.length)
        setClinicalEvents((previous) => [...previous, ...freshEvents]);
    }
  }, []);

  const { isConnected, sendData, closeConnection, reconnect } = useWebSocket({
    url: SCRIBE_V2_AUDIO_SOCKET_URL,
    onMessageReceived: handleFrame,
  });

  const hasConnectedRef = useRef<boolean>(false);
  if (isConnected) hasConnectedRef.current = true;

  /**
   * Frames sent on a closed socket are dropped silently, and a paused session
   * sends nothing for the server to keep alive, so a live session reopens it.
   */
  useEffect(() => {
    if (status === "LISTENING" && hasConnectedRef.current && !isConnected)
      reconnect();
  }, [status, isConnected, reconnect]);

  /** Every worklet frame is forwarded verbatim — the socket wants a continuous stream. */
  const handleAudioFrame = useCallback(
    (frame: ArrayBuffer) => sendData(frame),
    [sendData]
  );

  /**
   * Patient context is only useful once the audio socket is actually open, so
   * the request is gated on `isConnected` — it never fires while the scribe is
   * still connecting, and never at all if the connection fails. The hook logs
   * its own outcome; nothing here consumes the response yet.
   */
  usePatientScribeContext({ patientUuid, enabled: isConnected });

  const {
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    error,
  } = usePcmAudioStream({ onFrame: handleAudioFrame });

  const start = useCallback(() => {
    /** A finished session closes the socket — reopen it before the next one. */
    if (hasRecordedOnceRef.current) reconnect();
    hasRecordedOnceRef.current = true;

    sessionStartedAtRef.current = Date.now();
    seenEventIdsRef.current = new Set();
    lineCountRef.current = 0;
    setTranscript([]);
    setClinicalEvents([]);
    setStatus("LISTENING");
    void startRecording();
    startTimer();
  }, [reconnect, startRecording, startTimer]);

  const pause = useCallback(() => {
    pauseRecording();
    pauseTimer();
    setStatus("PAUSED");
  }, [pauseRecording, pauseTimer]);

  const resume = useCallback(() => {
    /** After a restore the audio graph is gone — re-acquire the mic instead of unpausing. */
    if (needsCaptureRestartRef.current) {
      needsCaptureRestartRef.current = false;
      void startRecording();
    } else {
      resumeRecording();
    }
    resumeTimer();
    setStatus("LISTENING");
  }, [resumeRecording, resumeTimer, startRecording]);

  const stop = useCallback(() => {
    stopRecording();
    closeConnection();
    stopTimer();
    setStatus("COMPLETED");
  }, [stopRecording, closeConnection, stopTimer]);

  const togglePause = useCallback(() => {
    if (status === "PAUSED") resume();
    else if (status === "LISTENING") pause();
  }, [status, pause, resume]);

  /**
   * Closing the scribe unmounts this hook, which must leave nothing running:
   * the microphone is released and the audio socket is closed. Re-opening
   * mounts a fresh hook, which opens a brand-new socket — so a session never
   * inherits a stale or half-closed connection.
   *
   * Held in a ref because the teardown must run exactly once, on unmount, and
   * not every time these callbacks are re-created.
   */
  const teardownRef = useRef<() => void>(() => undefined);
  teardownRef.current = () => {
    stopRecording();
    closeConnection();
  };
  useEffect(() => () => teardownRef.current(), []);

  return {
    status,
    setStatus,
    elapsedSeconds: time,
    transcript,
    clinicalEvents,
    error,
    /** Whether this session rehydrated from a persisted snapshot. */
    wasRestored,
    /** Wall-clock anchor (ms) — persisted so a recovered session keeps its time. */
    sessionStartedAt: sessionStartedAtRef.current,
    start,
    pause,
    resume,
    togglePause,
    stop,
  };
};
